import {
  WSButton,
  WSElement,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import { useEffect, useMemo, useRef } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { Tabs } from "../../../components/Tabs";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { INVOICING_PATH } from "../../Invoicing";
import { FilterableTableWorkLogsPayeeView } from "../components/TableWorkLogsPayeeView/FilterableTableWorkLogsPayeeView";
import { WorkLogPayeeTabFilterStatus } from "../components/TableWorkLogsPayeeView/filters/types";
import { useModalCreateWorkLog } from "../modals/ModalCreateWorkLog";
import {
  PATH_WORK_LOGS_PAYEE,
  PATH_WORK_LOGS_PAYEE_TAB_APPROVED,
  PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS,
  PATH_WORK_LOGS_PAYEE_TAB_REJECTED,
  PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED
} from "../paths";

const useOpenCreateWorkLogModalFromUrl = () => {
  const history = useHistory();
  const location = useLocation();
  const modalCreateWorkLog = useModalCreateWorkLog();
  const hasOpenedModalRef = useRef(false);

  useEffect(() => {
    if (hasOpenedModalRef.current) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const payerId = params.get("payerId");
    const engagementId = params.get("engagementId");

    if (payerId && engagementId) {
      hasOpenedModalRef.current = true;

      // Clear query params from URL
      history.replace(location.pathname);

      modalCreateWorkLog.open({
        defaultPayerId: payerId,
        defaultPayerPayeeEngagementId: engagementId
      });
    }
  }, [history, location.pathname, location.search, modalCreateWorkLog]);
};

export const RoutePayeeLogs = () => {
  const history = useHistory();
  const modalCreateWorkLog = useModalCreateWorkLog();
  const userId = useUserId();
  const featureFlags = useFeatureFlags();
  const isWorkLogReviewWorkflow = featureFlags.data?.workLogReviewWorkflow;

  useOpenCreateWorkLogModalFromUrl();

  // Summary queries for tab counts
  const queryInProgress = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": "InProgress" as WorkLogStatus
  });

  const querySubmitted = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": "InReview" as WorkLogStatus
  });

  const queryApproved = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": WorkLogStatus.Approved
  });

  const queryRejected = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": "Rejected" as WorkLogStatus
  });

  // Build tabs based on feature flag
  const tabs = useMemo(() => {
    if (isWorkLogReviewWorkflow) {
      // New experience: 4 tabs
      return [
        {
          label: "In progress",
          path: PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS,
          badge: queryInProgress.data?.listSize
        },
        {
          label: "Submitted",
          path: PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED,
          badge: querySubmitted.data?.listSize
        },
        {
          label: "Approved",
          path: PATH_WORK_LOGS_PAYEE_TAB_APPROVED,
          badge: queryApproved.data?.listSize
        },
        {
          label: "Rejected",
          path: PATH_WORK_LOGS_PAYEE_TAB_REJECTED,
          badge: queryRejected.data?.listSize
        }
      ];
    }

    // Legacy experience: 2 tabs
    return [
      {
        label: "In-progress",
        path: PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS,
        badge: queryInProgress.data?.listSize
      },
      {
        label: "Submitted",
        path: PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED,
        badge: querySubmitted.data?.listSize
      }
    ];
  }, [
    isWorkLogReviewWorkflow,
    queryInProgress.data?.listSize,
    querySubmitted.data?.listSize,
    queryApproved.data?.listSize,
    queryRejected.data?.listSize
  ]);

  // Default redirect path based on feature flag
  const defaultPath = PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS;

  return (
    <WSPage>
      <WSElement mb="2XL">
        <WSPageToolbar
          title="Work logs"
          actions={[
            {
              label: "New work log",
              buttonKind: "Primary",
              onClick: () => modalCreateWorkLog.open()
            }
          ]}
        />
        <WSText.ParagraphSm color="gray500">
          Track your work and submit for client approval. Approved work logs
          will generate invoices on your{" "}
          <WSButton.Link onClick={() => history.push(INVOICING_PATH)}>
            Invoices
          </WSButton.Link>{" "}
          page.
        </WSText.ParagraphSm>
      </WSElement>

      <Tabs mb="L" tabs={tabs} />

      <Switch>
        {/* New experience routes (workLogReviewWorkflow) */}
        {isWorkLogReviewWorkflow && (
          <Route path={PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS}>
            <FilterableTableWorkLogsPayeeView
              status={WorkLogPayeeTabFilterStatus.InProgress}
            />
          </Route>
        )}
        {isWorkLogReviewWorkflow && (
          <Route path={PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED}>
            <FilterableTableWorkLogsPayeeView
              status={WorkLogPayeeTabFilterStatus.Submitted}
            />
          </Route>
        )}
        {isWorkLogReviewWorkflow && (
          <Route path={PATH_WORK_LOGS_PAYEE_TAB_APPROVED}>
            <FilterableTableWorkLogsPayeeView
              status={WorkLogPayeeTabFilterStatus.Approved}
            />
          </Route>
        )}
        {isWorkLogReviewWorkflow && (
          <Route path={PATH_WORK_LOGS_PAYEE_TAB_REJECTED}>
            <FilterableTableWorkLogsPayeeView
              status={WorkLogPayeeTabFilterStatus.Rejected}
            />
          </Route>
        )}

        {/* Legacy experience routes */}
        {!isWorkLogReviewWorkflow && (
          <Route path={PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS}>
            <FilterableTableWorkLogsPayeeView
              status={WorkLogPayeeTabFilterStatus.InProgress}
            />
          </Route>
        )}
        {!isWorkLogReviewWorkflow && (
          <Route path={PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED}>
            <FilterableTableWorkLogsPayeeView
              status={WorkLogPayeeTabFilterStatus.Submitted}
            />
          </Route>
        )}

        <Redirect from={PATH_WORK_LOGS_PAYEE} to={defaultPath} />
      </Switch>
    </WSPage>
  );
};
